import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Table, Dropdown } from 'react-bootstrap';
import { FaCalendarAlt, FaFileAlt, FaPlus, FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import apiService from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const DEFAULT_FORM = {
  account_id: '',
  site_ids: [],
  group_ids: [],
  report_name: '',
  report_content: '',
  format: 'pdf',
  period_type: 'custom',
  start_date: '',
  end_date: '',
};

const ALL_SITES_VALUE = '__all_sites__';
const ALL_GROUPS_VALUE = '__all_groups__';

const REPORT_CONTENT_OPTIONS = [
  'Executive Insights',
  'Executive Insights By Group',
  'Mitigation and Response Insights',
  'Applications Insights',
  'Threats Insights',
];

const THREATS_INSIGHTS_SECTIONS = [
  'Threats Status',
  'Confidence level',
  'Analyst verdict',
  'Initiated by',
  'Detecting Engines',
  'Classifications',
  'Detection by operating system',
  'Top device at risk',
  'Top Groups at risk',
  'Top sites at risk',
  'Top indicators',
];

const APPLICATIONS_INSIGHTS_SECTIONS = [
  'Key Findings',
  'Top Windows Applications Of All Time',
  'Top New/Updated Windows Application During Report Period',
  'Top MacOS Applications of All Time',
  'Top New/Updated MacOS Application During Report Period',
  'Top Linux Applications of All Time',
  'Top New/Updated Linux Application During Report Period',
];

const toTitleCase = (value) => String(value || '')
  .trim()
  .replaceAll('_', ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const getThreatStatusLabel = (alert) =>
  toTitleCase(alert.metadata?.mitigation_status || alert.metadata?.incident_status || alert.status || 'Unknown');

const isMitigatedThreat = (alert) => {
  const analystVerdict = String(alert.metadata?.analyst_verdict || '').trim().toLowerCase();
  const mitigationStatus = String(alert.metadata?.mitigation_status || '').trim().toLowerCase();
  const incidentStatus = String(alert.metadata?.incident_status || '').trim().toLowerCase();
  const status = String(alert.status || '').trim().toLowerCase();
  if (analystVerdict === 'false_positive' || status === 'false_positive' || status === 'false positive') {
    return false;
  }
  return analystVerdict === 'true_positive'
    || mitigationStatus === 'success'
    || mitigationStatus === 'mitigated'
    || mitigationStatus === 'blocked'
    || mitigationStatus === 'resolved'
    || mitigationStatus === 'quarantined'
    || mitigationStatus === 'killed'
    || mitigationStatus === 'remediated'
    || incidentStatus === 'resolved'
    || status === 'resolved'
    || status === 'blocked';
};

const isMarkedAsBenignThreat = (alert) => {
  const analystVerdict = String(alert.metadata?.analyst_verdict || '').trim().toLowerCase();
  const mitigationStatus = String(alert.metadata?.mitigation_status || '').trim().toLowerCase();
  return analystVerdict === 'false_positive'
    || String(alert.status || '').trim().toLowerCase() === 'false_positive'
    || String(alert.status || '').trim().toLowerCase() === 'false positive'
    || mitigationStatus === 'marked_as_benign'
    || mitigationStatus === 'marked as benign';
};

const getConfidenceLevelLabel = (alert) => {
  const rawConfidence = String(alert.metadata?.confidence_level || '').trim().toLowerCase();
  if (rawConfidence === 'malicious') return 'Malicious';
  if (rawConfidence === 'suspicious') return 'Suspicious';
  if (rawConfidence === 'warning') return 'Warning';
  if (rawConfidence === 'benign') return 'Benign';

  const score = Number(alert.confidence ?? 0);
  if (score >= 80) return 'Malicious';
  if (score >= 50) return 'Suspicious';
  if (score > 0) return 'Low Confidence';
  return 'Unknown';
};

const getAnalystVerdictLabel = (alert) => {
  const status = String(alert.status || '').toLowerCase();
  if (status === 'resolved' || status === 'blocked') return 'True Positive';
  if (status === 'false_positive') return 'False Positive';
  if (status === 'active' || status === 'unresolved') return 'Suspicious';
  return 'Undefined';
};

const getClassificationLabel = (alert) => {
  const classification = String(alert.metadata?.classification || '').trim();
  if (classification) return toTitleCase(classification);
  if (alert.threat_type) return toTitleCase(alert.threat_type);
  return 'Unknown';
};

const getIndicatorLabel = (alert) => String(
  alert.recommendations?.[0]
    || alert.description
    || alert.threat_type
    || alert.process_name
    || 'Unknown'
).replaceAll('_', ' ');

const buildCountRows = (items = [], limit = 5) => Object.entries(
  items.reduce((acc, item) => {
    const key = String(item || 'Unknown').trim() || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {})
)
  .map(([label, value]) => ({ label, value }))
  .sort((left, right) => right.value - left.value || left.label.localeCompare(right.label))
  .slice(0, limit);

const buildTopRiskRows = (items = [], limit = 5) => Object.entries(
  items.reduce((acc, item) => {
    const key = String(item || 'Unknown').trim() || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {})
)
  .map(([name, threats]) => ({ name, threats }))
  .sort((left, right) => right.threats - left.threats || left.name.localeCompare(right.name))
  .slice(0, limit);

const getThreatKey = (alert) => String(alert.threat_type || alert.process_name || alert.id || 'Unknown').trim();

const getPolicyModeLabel = (alert, endpointsByName) =>
  endpointsByName.get(alert.endpoint_name)?.policy_mode
  || alert.metadata?.policy_mode
  || '-';

const getInitiatedByLabel = (alert) => {
  const source = `${alert.metadata?.initiated_by || ''} ${alert.metadata?.scan_type || ''} ${alert.metadata?.source || ''} ${alert.metadata?.origin || ''} ${alert.metadata?.triggered_by || ''} ${alert.description || ''}`.toLowerCase();
  if (source.includes('policy')) return 'Agent Policy';
  if (source.includes('full disk scan') || source.includes('full_scan')) return 'Full Disk Scan';
  if (source.includes('sentinelctl')) return 'SentinelCTL';
  if (source.includes('dv command') || source.includes('dvcmd')) return 'DV Command';
  if (source.includes('console api') || source.includes(' api')) return 'Console API';
  return '';
};

const buildTopDeviceRows = (alerts = [], endpoints = [], limit = 5) => {
  const endpointsByName = new Map(endpoints.map((endpoint) => [endpoint.name, endpoint]));
  const grouped = alerts.reduce((acc, alert) => {
    const name = String(alert.endpoint_name || 'Unknown').trim() || 'Unknown';
    if (!acc[name]) {
      acc[name] = {
        name,
        threats: 0,
        uniqueThreats: new Set(),
        policyMode: getPolicyModeLabel(alert, endpointsByName),
      };
    }
    acc[name].threats += 1;
    acc[name].uniqueThreats.add(getThreatKey(alert));
    if (acc[name].policyMode === '-') {
      acc[name].policyMode = getPolicyModeLabel(alert, endpointsByName);
    }
    return acc;
  }, {});

  return Object.values(grouped)
    .map((item) => ({
      name: item.name,
      threats: item.threats,
      uniqueThreats: item.uniqueThreats.size,
      policyMode: item.policyMode,
    }))
    .sort((left, right) => right.threats - left.threats || left.name.localeCompare(right.name))
    .slice(0, limit);
};

const buildTopIndicatorRows = (alerts = [], limit = 5) => {
  const grouped = alerts.reduce((acc, alert) => {
    const indicator = getIndicatorLabel(alert);
    if (!acc[indicator]) {
      acc[indicator] = {
        indicator,
        occurrences: 0,
        agents: {},
      };
    }
    acc[indicator].occurrences += 1;
    const agent = String(alert.endpoint_name || 'Unknown').trim() || 'Unknown';
    acc[indicator].agents[agent] = (acc[indicator].agents[agent] || 0) + 1;
    return acc;
  }, {});

  return Object.values(grouped)
    .map((item) => ({
      indicator: item.indicator,
      occurrences: item.occurrences,
      mostSeenAgent: Object.entries(item.agents).sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))[0]?.[0] || 'Unknown',
    }))
    .sort((left, right) => right.occurrences - left.occurrences || left.indicator.localeCompare(right.indicator))
    .slice(0, limit);
};

const getScopePeriodKey = (startDate, endDate) => {
  if (!startDate || !endDate) return '';

  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';

  const diffMs = Math.max(0, end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1;

  if (diffDays <= 1) return 'daily';
  if (diffDays <= 7) return 'weekly';
  if (diffDays <= 30) return 'monthly';
  if (diffDays <= 366) return 'yearly';
  return 'all_time';
};

const buildThreatsInsightsPreview = ({ alerts = [], endpoints = [], activities = [], accountName, siteNames, groupNames, startDate, endDate }) => ({
  createdAt: new Date().toLocaleString(),
  reportPeriod: `${startDate} to ${endDate}`,
  reportScope: [
    accountName,
    siteNames.length > 0 ? `Sites: ${siteNames.join(', ')}` : '',
    groupNames.length > 0 ? `Groups: ${groupNames.join(', ')}` : '',
  ].filter(Boolean).join(' / '),
  overview: {
    threatsFoundOnEndpoints: new Set(alerts.map((alert) => alert.endpoint_name).filter(Boolean)).size,
    totalThreatsSinceDeployment: alerts.length,
  },
  threatsStatus: {
    mitigated: alerts.filter((alert) => isMitigatedThreat(alert)).length,
    notMitigated: alerts.filter((alert) => {
      return !isMitigatedThreat(alert) && !isMarkedAsBenignThreat(alert);
    }).length,
    markedAsBenign: alerts.filter((alert) => isMarkedAsBenignThreat(alert)).length,
  },
  incidentStats: {
    unresolved: alerts.filter((alert) => {
      const incidentStatus = String(alert.metadata?.incident_status || '').toLowerCase();
      const status = String(alert.status || '').toLowerCase();
      return incidentStatus !== 'resolved' && incidentStatus !== 'in progress' && incidentStatus !== 'under investigation' && status !== 'resolved';
    }).length,
    resolved: alerts.filter((alert) => {
      const incidentStatus = String(alert.metadata?.incident_status || '').toLowerCase();
      const status = String(alert.status || '').toLowerCase();
      return incidentStatus === 'resolved' || status === 'resolved';
    }).length,
    inProgress: alerts.filter((alert) => {
      const incidentStatus = String(alert.metadata?.incident_status || '').toLowerCase();
      return incidentStatus === 'in progress' || incidentStatus === 'under investigation';
    }).length,
  },
  confidenceLevels: buildCountRows(alerts.map((alert) => getConfidenceLevelLabel(alert))),
  analystVerdicts: {
    truePositive: alerts.filter((alert) => getAnalystVerdictLabel(alert) === 'True Positive').length,
    falsePositive: alerts.filter((alert) => getAnalystVerdictLabel(alert) === 'False Positive').length,
    suspicious: alerts.filter((alert) => getAnalystVerdictLabel(alert) === 'Suspicious').length,
    undefined: alerts.filter((alert) => getAnalystVerdictLabel(alert) === 'Undefined').length,
  },
  initiatedBy: {
    agentPolicy: alerts.filter((alert) => getInitiatedByLabel(alert) === 'Agent Policy').length,
    fullDiskScan: alerts.filter((alert) => getInitiatedByLabel(alert) === 'Full Disk Scan').length,
    sentinelCTL: alerts.filter((alert) => getInitiatedByLabel(alert) === 'SentinelCTL').length,
    dvCommand: alerts.filter((alert) => getInitiatedByLabel(alert) === 'DV Command').length,
    consoleAPI: alerts.filter((alert) => getInitiatedByLabel(alert) === 'Console API').length,
  },
  detectingEngines: buildCountRows(alerts.map((alert) => alert.provider_name || alert.provider || 'Unknown')),
  classifications: buildCountRows(alerts.map((alert) => getClassificationLabel(alert))),
  detectionsByOperatingSystem: buildCountRows(alerts.map((alert) => alert.endpoint_os || 'Unknown')),
  topDevicesAtRisk: buildTopDeviceRows(alerts, endpoints),
  topGroupsAtRisk: buildTopRiskRows(alerts.map((alert) => alert.metadata?.group_name || alert.metadata?.group_id || 'Unknown')),
  topSitesAtRisk: buildTopRiskRows(alerts.map((alert) => alert.metadata?.site_name || alert.metadata?.site_id || 'Unknown')),
  topIndicators: buildTopIndicatorRows(alerts),
  totals: {
    alerts: alerts.length,
    endpoints: endpoints.length,
    activities: activities.length,
  },
});

const PreviewTable = ({ title, rows, emptyLabel = 'No data found.' }) => (
  <Card className="h-100">
    <Card.Header>{title}</Card.Header>
    <Card.Body>
      {rows.length === 0 ? (
        <div className="text-muted">{emptyLabel}</div>
      ) : (
        <div className="table-responsive">
          <Table size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Item</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${title}-${row.label}`}>
                  <td>{row.label}</td>
                  <td>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card.Body>
  </Card>
);

const SummaryListCard = ({ title, rows }) => (
  <Card className="h-100">
    <Card.Header>{title}</Card.Header>
    <Card.Body>
      {rows.map((row) => (
        <div key={row.label} className="d-flex justify-content-between border-bottom py-2">
          <span>{row.label}</span>
          <strong>{row.value}</strong>
        </div>
      ))}
    </Card.Body>
  </Card>
);

const TopDevicesTable = ({ rows }) => (
  <Card className="h-100">
    <Card.Header>Top device at risk</Card.Header>
    <Card.Body>
      {rows.length === 0 ? (
        <div className="text-muted">No data found.</div>
      ) : (
        <div className="table-responsive">
          <Table size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Threats</th>
                <th>Unique Threats</th>
                <th>Policy Mode</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name}>
                  <td>{row.name}</td>
                  <td>{row.threats}</td>
                  <td>{row.uniqueThreats}</td>
                  <td>{row.policyMode}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card.Body>
  </Card>
);

const TopIndicatorsTable = ({ rows }) => (
  <Card className="h-100">
    <Card.Header>Top indicators</Card.Header>
    <Card.Body>
      {rows.length === 0 ? (
        <div className="text-muted">No indicator data found.</div>
      ) : (
        <div className="table-responsive">
          <Table size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Occurrences</th>
                <th>Most Seen Agent</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.indicator}>
                  <td>{row.indicator}</td>
                  <td>{row.occurrences}</td>
                  <td>{row.mostSeenAgent}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card.Body>
  </Card>
);

const getToday = () => new Date().toISOString().slice(0, 10);

const getDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

const applyPeriodType = (periodType) => {
  switch (periodType) {
    case 'last_7_days':
      return { start_date: getDaysAgo(6), end_date: getToday() };
    case 'last_30_days':
      return { start_date: getDaysAgo(29), end_date: getToday() };
    case 'this_month': {
      const date = new Date();
      const start = new Date(date.getFullYear(), date.getMonth(), 1);
      return {
        start_date: start.toISOString().slice(0, 10),
        end_date: getToday(),
      };
    }
    case 'custom':
    default:
      return { start_date: '', end_date: '' };
  }
};

const Report = () => {
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingTask, setSavingTask] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [hierarchy, setHierarchy] = useState({ accounts: [], sites: [], groups: [] });
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [tasks, setTasks] = useState([]);
  const [threatsPreview, setThreatsPreview] = useState(null);
  const [threatsPreviewError, setThreatsPreviewError] = useState('');
  const [loadingThreatsPreview, setLoadingThreatsPreview] = useState(false);

  useEffect(() => {
    const loadHierarchy = async () => {
      try {
        setLoading(true);
        const hierarchyResponse = await apiService.getEDRHierarchy();
        setHierarchy({
          accounts: hierarchyResponse.data?.accounts || hierarchyResponse.accounts || [],
          sites: hierarchyResponse.data?.sites || hierarchyResponse.sites || [],
          groups: hierarchyResponse.data?.groups || hierarchyResponse.groups || [],
        });
      } catch (loadError) {
        setError('Failed to load report scope data.');
      } finally {
        setLoading(false);
      }
    };

    loadHierarchy();
  }, []);

  const filteredSites = useMemo(() => {
    if (!formData.account_id) return [];
    return hierarchy.sites.filter((site) => site.account_id === formData.account_id);
  }, [formData.account_id, hierarchy.sites]);

  const filteredGroups = useMemo(() => {
    if (formData.site_ids.length === 0) return [];
    return hierarchy.groups.filter((group) => formData.site_ids.includes(group.site_id));
  }, [formData.site_ids, hierarchy.groups]);

  const selectedAccount = hierarchy.accounts.find((account) => account.id === formData.account_id);
  const selectedSites = filteredSites.filter((site) => formData.site_ids.includes(site.id));
  const selectedGroups = filteredGroups.filter((group) => formData.group_ids.includes(group.id));
  const selectedSiteNames = selectedSites.map((site) => site.name);
  const selectedGroupNames = selectedGroups.map((group) => group.name);

  const openTaskModal = () => {
    setSuccessMessage('');
    setError('');
    setFormData(DEFAULT_FORM);
    setThreatsPreview(null);
    setThreatsPreviewError('');
    setShowTaskModal(true);
  };

  const closeTaskModal = () => {
    setShowTaskModal(false);
  };

  const handleSiteCheck = (siteId, checked) => {
    setFormData((prev) => {
      const newSiteIds = checked
        ? [...prev.site_ids, siteId]
        : prev.site_ids.filter((id) => id !== siteId);
      return {
        ...prev,
        site_ids: newSiteIds,
        group_ids: [], // Reset groups when sites change
      };
    });
    setThreatsPreview(null);
    setThreatsPreviewError('');
  };

  const handleSiteCheckAll = (checked) => {
    setFormData((prev) => ({
      ...prev,
      site_ids: checked ? filteredSites.map((site) => site.id) : [],
      group_ids: [],
    }));
    setThreatsPreview(null);
    setThreatsPreviewError('');
  };

  const handleGroupCheck = (groupId, checked) => {
    setFormData((prev) => ({
      ...prev,
      group_ids: checked
        ? [...prev.group_ids, groupId]
        : prev.group_ids.filter((id) => id !== groupId),
    }));
    setThreatsPreview(null);
    setThreatsPreviewError('');
  };

  const handleGroupCheckAll = (checked) => {
    setFormData((prev) => ({
      ...prev,
      group_ids: checked ? filteredGroups.map((group) => group.id) : [],
    }));
    setThreatsPreview(null);
    setThreatsPreviewError('');
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (name === 'account_id') {
      setFormData((prev) => ({
        ...prev,
        account_id: value,
        site_ids: [],
        group_ids: [],
      }));
      setThreatsPreview(null);
      setThreatsPreviewError('');
      return;
    }

    if (name === 'period_type') {
      setFormData((prev) => ({
        ...prev,
        period_type: value,
        ...applyPeriodType(value),
      }));
      setThreatsPreview(null);
      setThreatsPreviewError('');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === 'report_content' || name === 'start_date' || name === 'end_date') {
      setThreatsPreview(null);
      setThreatsPreviewError('');
    }
  };

  const handleFetchThreatsPreview = async () => {
    if (
      !formData.account_id
      || formData.site_ids.length === 0
      || formData.group_ids.length === 0
      || !formData.start_date
      || !formData.end_date
    ) {
      setThreatsPreviewError('Select account, one or more sites, one or more groups, start date, and end date first.');
      return;
    }

    if (formData.start_date > formData.end_date) {
      setThreatsPreviewError('Start date must be earlier than or equal to end date.');
      return;
    }

    const period = getScopePeriodKey(formData.start_date, formData.end_date);
    if (!period) {
      setThreatsPreviewError('Unable to determine a supported report period from the selected dates.');
      return;
    }

    try {
      setLoadingThreatsPreview(true);
      setThreatsPreviewError('');
      const response = await apiService.getEDRReportData({
        account_id: formData.account_id,
        site_ids: formData.site_ids.join(','),
        group_ids: formData.group_ids.join(','),
        period,
      });

      setThreatsPreview(buildThreatsInsightsPreview({
        alerts: response.alerts || [],
        endpoints: response.endpoints || [],
        activities: response.activities || [],
        accountName: selectedAccount?.name || '',
        siteNames: selectedSiteNames,
        groupNames: selectedGroupNames,
        startDate: formData.start_date,
        endDate: formData.end_date,
      }));
    } catch (fetchError) {
      setThreatsPreviewError('Failed to pick up Threats Insights data for the selected scope.');
    } finally {
      setLoadingThreatsPreview(false);
    }
  };

  const handleCreateTask = async () => {
    if (
      !formData.account_id
      || formData.site_ids.length === 0
      || formData.group_ids.length === 0
      || !formData.report_name.trim()
      || !formData.report_content.trim()
      || !formData.start_date
      || !formData.end_date
    ) {
      setError('Please complete all required report task fields.');
      return;
    }

    if (formData.start_date > formData.end_date) {
      setError('Start date must be earlier than or equal to end date.');
      return;
    }

    try {
      setSavingTask(true);
      setError('');

      const period = getScopePeriodKey(formData.start_date, formData.end_date);
      if (!period) {
        setError('Unable to determine a supported report period from the selected dates.');
        return;
      }

      const submitData = new FormData();
      submitData.set('edr_provider', selectedSites[0]?.provider_id || selectedAccount?.provider_id || '');
      submitData.set('account_id', formData.account_id);
      submitData.set('report_name', formData.report_name.trim());
      submitData.set('report_content', formData.report_content.trim());
      submitData.set('site_id', formData.site_ids[0] || '');
      submitData.set('group_id', formData.group_ids[0] || '');
      submitData.set('format', formData.format);
      submitData.set('language', 'en');
      submitData.set('theme_color', '#2f5496');
      submitData.set('period', period);
      submitData.set('start_date', formData.start_date);
      submitData.set('end_date', formData.end_date);
      formData.site_ids.forEach((siteId) => submitData.append('site_ids', siteId));
      formData.group_ids.forEach((groupId) => submitData.append('group_ids', groupId));

      await apiService.downloadReportTaskPdf(
        submitData,
        formData.report_name.trim()
      );

      const newTask = {
        id: `task-${Date.now()}`,
        report_name: formData.report_name.trim(),
        report_content: formData.report_content.trim(),
        account_name: selectedAccount?.name || '-',
        site_names: selectedSiteNames,
        group_names: selectedGroupNames,
        format: formData.format.toUpperCase(),
        start_date: formData.start_date,
        end_date: formData.end_date,
        created_at: new Date().toISOString(),
        status: 'Downloaded',
        report_scope: [
          selectedAccount?.name,
          selectedSiteNames.length > 0 ? `Sites: ${selectedSiteNames.join(', ')}` : '',
          selectedGroupNames.length > 0 ? `Groups: ${selectedGroupNames.join(', ')}` : '',
        ].filter(Boolean).join(' / '),
        details: formData.report_content === 'Threats Insights'
          ? (threatsPreview || {
              createdAt: new Date().toLocaleString(),
              reportPeriod: `${formData.start_date} to ${formData.end_date}`,
              reportScope: [
                selectedAccount?.name,
                selectedSiteNames.length > 0 ? `Sites: ${selectedSiteNames.join(', ')}` : '',
                selectedGroupNames.length > 0 ? `Groups: ${selectedGroupNames.join(', ')}` : '',
              ].filter(Boolean).join(' / '),
              overview: {
                threatsFoundOnEndpoints: 0,
                totalThreatsSinceDeployment: 0,
              },
              threatsStatus: {
                mitigated: 0,
                notMitigated: 0,
                markedAsBenign: 0,
              },
              incidentStats: {
                unresolved: 0,
                resolved: 0,
                inProgress: 0,
              },
              confidenceLevels: [],
              analystVerdicts: {
                truePositive: 0,
                falsePositive: 0,
                suspicious: 0,
                undefined: 0,
              },
              initiatedBy: {
                agentPolicy: 0,
                fullDiskScan: 0,
                sentinelCTL: 0,
                dvCommand: 0,
                consoleAPI: 0,
              },
              detectingEngines: [],
              classifications: [],
              detectionsByOperatingSystem: [],
              topDevicesAtRisk: [],
              topGroupsAtRisk: [],
              topSitesAtRisk: [],
              topIndicators: [],
              totals: { alerts: 0, endpoints: 0, activities: 0 },
            })
          : null,
      };

      setTasks((prev) => [newTask, ...prev]);
      setSuccessMessage(`Report "${newTask.report_name}" was generated and downloaded as ${newTask.format}.`);
      setShowTaskModal(false);
    } catch (downloadError) {
      setError('Failed to generate and download the report PDF. Please try again.');
    } finally {
      setSavingTask(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading report page..." />;
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1 text-heading-1">
                {/* <FaFileAlt className="me-2 text-primary" /> */}
                Task Report
              </h2>
              <p className="text-muted mb-0">Manage and generate comprehensive security reports</p>
            </div>
            <Button variant="primary" onClick={openTaskModal} className="d-flex align-items-center">
              <FaPlus className="me-2" />
              New Report Task
            </Button>
          </div>

          {error && (
            <Alert variant="danger" className="d-flex align-items-center">
              <span className="material-symbols-outlined me-2">error</span>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert variant="success" className="d-flex align-items-center">
              <span className="material-symbols-outlined me-2">check_circle</span>
              {successMessage}
            </Alert>
          )}

      <Row className="g-4 mb-4">
        <Col md={4}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                  <span className="material-symbols-outlined text-primary fs-4">assignment</span>
                </div>
              </div>
              <div className="ms-3">
                <div className="text-muted small mb-1">Total Tasks</div>
                <div className="fs-3 fw-bold text-primary">{tasks.length}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                  <span className="material-symbols-outlined text-success fs-4">picture_as_pdf</span>
                </div>
              </div>
              <div className="ms-3">
                <div className="text-muted small mb-1">PDF Format</div>
                <div className="fs-3 fw-bold text-success">Enabled</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                  <span className="material-symbols-outlined text-info fs-4">history</span>
                </div>
              </div>
              <div className="ms-3">
                <div className="text-muted small mb-1">Latest Task</div>
                <div className="fw-bold text-info">{tasks[0]?.report_name || 'No tasks yet'}</div>
                {tasks[0] && (
                  <div className="small text-muted">
                    {new Date(tasks[0].created_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center bg-white border-0">
          <div className="d-flex align-items-center">
            <span className="material-symbols-outlined me-2 text-primary">list_alt</span>
            <span className="fw-semibold">Report Tasks</span>
          </div>
          <Badge bg="primary" className="rounded-pill">{tasks.length}</Badge>
        </Card.Header>
        <Card.Body className="p-0">
          {tasks.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <span className="material-symbols-outlined fs-1 text-muted">inbox</span>
              </div>
              <div className="text-muted mb-3">
                No report tasks yet. Click <strong>New Report Task</strong> to create one.
              </div>
              <Button variant="outline-primary" onClick={openTaskModal}>
                {/* <FaPlus className="me-2" /> */}
                Create Your First Report
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 border-0">
                <thead className="bg-light">
                  <tr>
                    <th className="border-0 fw-semibold">Report Name</th>
                    <th className="border-0 fw-semibold">Account</th>
                    <th className="border-0 fw-semibold">Site</th>
                    <th className="border-0 fw-semibold">Group</th>
                    <th className="border-0 fw-semibold">Format</th>
                    <th className="border-0 fw-semibold">Time Period</th>
                    <th className="border-0 fw-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => (
                    <tr key={task.id} className="border-bottom">
                      <td className="py-3">
                        <div className="d-flex align-items-start">
                          <span className="material-symbols-outlined me-2 text-muted">description</span>
                          <div>
                            <div className="fw-semibold text-body">{task.report_name}</div>
                            <div className="small text-muted">{task.report_content}</div>
                            {task.details && (
                              <div className="small text-muted mt-1">
                                <span className="material-symbols-outlined fs-6 me-1">location_on</span>
                                {task.details.reportScope}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="d-flex align-items-center">
                          <span className="material-symbols-outlined me-1 text-muted">account_tree</span>
                          {task.account_name}
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="d-flex align-items-center">
                          <span className="material-symbols-outlined me-1 text-muted">location_on</span>
                          {task.site_names?.join(', ') || '-'}
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="d-flex align-items-center">
                          <span className="material-symbols-outlined me-1 text-muted">groups</span>
                          {task.group_names?.join(', ') || '-'}
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="d-flex align-items-center">
                          <span className="material-symbols-outlined me-1 text-muted">picture_as_pdf</span>
                          <Badge bg="light" text="dark" className="rounded-pill">{task.format}</Badge>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="d-flex align-items-center">
                          <span className="material-symbols-outlined me-1 text-muted">date_range</span>
                          <div>
                            <div className="small">{task.start_date}</div>
                            <div className="small text-muted">to {task.end_date}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <Badge 
                          bg={task.status === 'Downloaded' ? 'success' : 'warning'} 
                          text={task.status === 'Downloaded' ? undefined : 'dark'}
                          className="rounded-pill d-flex align-items-center"
                        >
                          <span className="material-symbols-outlined me-1 fs-6">
                            {task.status === 'Downloaded' ? 'check_circle' : 'pending'}
                          </span>
                          {task.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showTaskModal} onHide={closeTaskModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFileAlt className="me-2" />
            New Report Task
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Account Name</Form.Label>
                  <Dropdown autoClose="outside">
                    <Dropdown.Toggle
                      className="report-dropdown-toggle w-100 text-start"
                    >
                      <span className="text-truncate">
                        {formData.account_id ? selectedAccount?.name || 'Select account' : 'Select account'}
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="report-dropdown-menu w-100">
                      {hierarchy.accounts.map((account) => (
                        <button
                          key={account.id}
                          type="button"
                          className={`report-dropdown-item ${formData.account_id === account.id ? 'selected' : ''}`}
                          onClick={() => handleInputChange({ target: { name: 'account_id', value: account.id } })}
                        >
                          <span className="report-dropdown-icon">
                            {formData.account_id === account.id ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                          </span>
                          <span>{account.name}</span>
                        </button>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Site Name</Form.Label>
                  <div
                    style={{
                      maxHeight: '120px',
                      overflowY: 'auto',
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      padding: '0.5rem',
                      backgroundColor: !formData.account_id ? '#e9ecef' : '#fff',
                    }}
                  >
                    {filteredSites.length > 0 && (
                      <Form.Check
                        type="checkbox"
                        id="site-all"
                        label="All Sites"
                        checked={formData.site_ids.length === filteredSites.length && filteredSites.length > 0}
                        onChange={(e) => handleSiteCheckAll(e.target.checked)}
                        disabled={!formData.account_id}
                        style={{ marginBottom: '0.5rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.25rem' }}
                      />
                    )}
                    {filteredSites.map((site) => (
                      <Form.Check
                        type="checkbox"
                        id={`site-${site.id}`}
                        key={site.id}
                        label={site.name}
                        checked={formData.site_ids.includes(site.id)}
                        onChange={(e) => handleSiteCheck(site.id, e.target.checked)}
                        disabled={!formData.account_id}
                        style={{ marginBottom: '0.25rem' }}
                      />
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    {formData.site_ids.length} site(s) selected
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Group Name</Form.Label>
                  <div
                    style={{
                      maxHeight: '120px',
                      overflowY: 'auto',
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      padding: '0.5rem',
                      backgroundColor: formData.site_ids.length === 0 ? '#e9ecef' : '#fff',
                    }}
                  >
                    {filteredGroups.length > 0 && (
                      <Form.Check
                        type="checkbox"
                        id="group-all"
                        label="All Groups"
                        checked={formData.group_ids.length === filteredGroups.length && filteredGroups.length > 0}
                        onChange={(e) => handleGroupCheckAll(e.target.checked)}
                        disabled={formData.site_ids.length === 0}
                        style={{ marginBottom: '0.5rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.25rem' }}
                      />
                    )}
                    {filteredGroups.map((group) => (
                      <Form.Check
                        type="checkbox"
                        id={`group-${group.id}`}
                        key={group.id}
                        label={group.name}
                        checked={formData.group_ids.includes(group.id)}
                        onChange={(e) => handleGroupCheck(group.id, e.target.checked)}
                        disabled={formData.site_ids.length === 0}
                        style={{ marginBottom: '0.25rem' }}
                      />
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    {formData.group_ids.length} group(s) selected
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Report Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="report_name"
                    value={formData.report_name}
                    onChange={handleInputChange}
                    placeholder="Monthly executive summary"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Report Format</Form.Label>
                  <Dropdown autoClose="outside">
                    <Dropdown.Toggle
                      className="report-dropdown-toggle w-100 text-start"
                    >
                      <span className="text-truncate">
                        {formData.format === 'pdf' ? 'PDF' : 'PDF'}
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="report-dropdown-menu w-100">
                      <button
                        type="button"
                        className={`report-dropdown-item ${formData.format === 'pdf' ? 'selected' : ''}`}
                        onClick={() => handleInputChange({ target: { name: 'format', value: 'pdf' } })}
                      >
                        <span className="report-dropdown-icon">
                          {formData.format === 'pdf' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                        </span>
                        <span>PDF</span>
                      </button>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Form.Text className="text-muted">
                    PDF is the first supported format for the Report page.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Report Content</Form.Label>
                  <Dropdown autoClose="outside">
                    <Dropdown.Toggle
                      className="report-dropdown-toggle w-100 text-start"
                    >
                      <span className="text-truncate">
                        {formData.report_content || 'Select report content'}
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="report-dropdown-menu w-100">
                      {REPORT_CONTENT_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`report-dropdown-item ${formData.report_content === option ? 'selected' : ''}`}
                          onClick={() => handleInputChange({ target: { name: 'report_content', value: option } })}
                        >
                          <span className="report-dropdown-icon">
                            {formData.report_content === option ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                          </span>
                          <span>{option}</span>
                        </button>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Form.Group>
              </Col>

              {formData.report_content === 'Threats Insights' && (
                <Col md={12}>
                  <Card className="border-0 bg-light">
                    <Card.Body>
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
                        <div className="fw-semibold">Threats Insights Includes</div>
                        <Button variant="outline-primary" onClick={handleFetchThreatsPreview} disabled={loadingThreatsPreview}>
                          {loadingThreatsPreview ? 'Picking Up Data...' : 'Pick Up Threats Data'}
                        </Button>
                      </div>
                      {threatsPreviewError && <Alert variant="danger">{threatsPreviewError}</Alert>}
                      <Alert variant="secondary">
                        The backend currently supports relative periods only. Your selected date range is mapped to the nearest supported scope for data pick up.
                      </Alert>
                      <Row className="g-3 mb-3">
                        <Col md={4}>
                          <div className="small text-muted">Created at</div>
                          <div>{threatsPreview?.createdAt || new Date().toLocaleString()}</div>
                        </Col>
                        <Col md={4}>
                          <div className="small text-muted">Report period</div>
                          <div>{threatsPreview?.reportPeriod || (formData.start_date && formData.end_date ? `${formData.start_date} to ${formData.end_date}` : 'Select dates below')}</div>
                        </Col>
                        <Col md={4}>
                          <div className="small text-muted">Report scope</div>
                          <div>{threatsPreview?.reportScope || ([
                            selectedAccount?.name,
                            selectedSiteNames.length > 0 ? `Sites: ${selectedSiteNames.join(', ')}` : '',
                            selectedGroupNames.length > 0 ? `Groups: ${selectedGroupNames.join(', ')}` : '',
                          ].filter(Boolean).join(' / ') || 'Select account, sites, and groups')}</div>
                        </Col>
                      </Row>
                      <Row className="g-2 mb-3">
                        {THREATS_INSIGHTS_SECTIONS.map((section) => (
                          <Col md={6} key={section}>
                            <div className="border rounded px-3 py-2 bg-white">
                              {section}
                            </div>
                          </Col>
                        ))}
                      </Row>
                      {threatsPreview && (
                        <>
                          <Row className="g-3 mb-3">
                            <Col md={4}>
                              <Card>
                                <Card.Body>
                                  <div className="text-muted small">Alerts Picked Up</div>
                                  <div className="fs-4 fw-bold">{threatsPreview.totals.alerts}</div>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col md={4}>
                              <Card>
                                <Card.Body>
                                  <div className="text-muted small">Endpoints Picked Up</div>
                                  <div className="fs-4 fw-bold">{threatsPreview.totals.endpoints}</div>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col md={4}>
                              <Card>
                                <Card.Body>
                                  <div className="text-muted small">Activities Picked Up</div>
                                  <div className="fs-4 fw-bold">{threatsPreview.totals.activities}</div>
                                </Card.Body>
                              </Card>
                            </Col>
                          </Row>
                          <Row className="g-3">
                            <Col lg={6}>
                              <SummaryListCard
                                title="Overview"
                                rows={[
                                  { label: 'Threats found on endpoints', value: threatsPreview.overview.threatsFoundOnEndpoints },
                                  { label: 'Total threats since deployment', value: threatsPreview.overview.totalThreatsSinceDeployment },
                                ]}
                              />
                            </Col>
                            <Col lg={6}>
                              <SummaryListCard
                                title="Threats Status"
                                rows={[
                                  { label: 'Mitigated', value: threatsPreview.threatsStatus.mitigated },
                                  { label: 'Not mitigated', value: threatsPreview.threatsStatus.notMitigated },
                                  { label: 'Marked as benign', value: threatsPreview.threatsStatus.markedAsBenign },
                                ]}
                              />
                            </Col>
                            <Col lg={6}>
                              <SummaryListCard
                                title="Incidents Stats"
                                rows={[
                                  { label: 'Unresolved', value: threatsPreview.incidentStats.unresolved },
                                  { label: 'Resolved', value: threatsPreview.incidentStats.resolved },
                                  { label: 'In progress', value: threatsPreview.incidentStats.inProgress },
                                ]}
                              />
                            </Col>
                            <Col lg={6}><PreviewTable title="Confidence level" rows={threatsPreview.confidenceLevels} /></Col>
                            <Col lg={6}>
                              <SummaryListCard
                                title="Analyst verdict"
                                rows={[
                                  { label: 'True Positive', value: threatsPreview.analystVerdicts.truePositive },
                                  { label: 'False Positive', value: threatsPreview.analystVerdicts.falsePositive },
                                  { label: 'Suspicious', value: threatsPreview.analystVerdicts.suspicious },
                                  { label: 'Undefined', value: threatsPreview.analystVerdicts.undefined },
                                ]}
                              />
                            </Col>
                            <Col lg={6}>
                              <SummaryListCard
                                title="Initiated by"
                                rows={[
                                  { label: 'Agent policy', value: threatsPreview.initiatedBy.agentPolicy },
                                  { label: 'Full disk scan', value: threatsPreview.initiatedBy.fullDiskScan },
                                  { label: 'SentinelCTL', value: threatsPreview.initiatedBy.sentinelCTL },
                                  { label: 'DV command', value: threatsPreview.initiatedBy.dvCommand },
                                  { label: 'Console API', value: threatsPreview.initiatedBy.consoleAPI },
                                ]}
                              />
                            </Col>
                            <Col lg={6}><PreviewTable title="Detecting Engines" rows={threatsPreview.detectingEngines} /></Col>
                            <Col lg={6}><PreviewTable title="Classifications" rows={threatsPreview.classifications} /></Col>
                            <Col lg={6}><PreviewTable title="Detection by operating system" rows={threatsPreview.detectionsByOperatingSystem} /></Col>
                            <Col lg={12}><TopDevicesTable rows={threatsPreview.topDevicesAtRisk} /></Col>
                            <Col lg={6}><PreviewTable title="Top Groups at risk" rows={threatsPreview.topGroupsAtRisk.map((row) => ({ label: row.name, value: row.threats }))} /></Col>
                            <Col lg={6}><PreviewTable title="Top sites at risk" rows={threatsPreview.topSitesAtRisk.map((row) => ({ label: row.name, value: row.threats }))} /></Col>
                            <Col lg={12}><TopIndicatorsTable rows={threatsPreview.topIndicators} /></Col>
                          </Row>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              )}

              <Col md={12}>
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <FaCalendarAlt />
                      <div className="fw-semibold">Time Period With Pick Up Data</div>
                    </div>
                    <Row className="g-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Period</Form.Label>
                          <Form.Select name="period_type" value={formData.period_type} onChange={handleInputChange}>
                            <option value="custom">Custom Range</option>
                            <option value="last_7_days">Last 7 Days</option>
                            <option value="last_30_days">Last 30 Days</option>
                            <option value="this_month">This Month</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Start Date</Form.Label>
                          <Form.Control
                            type="date"
                            name="start_date"
                            value={formData.start_date}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>End Date</Form.Label>
                          <Form.Control
                            type="date"
                            name="end_date"
                            value={formData.end_date}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeTaskModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateTask} disabled={savingTask}>
            {savingTask ? 'Generating PDF...' : 'Create And Download PDF'}
          </Button>
        </Modal.Footer>
      </Modal>
        </div>
      </div>
    </div>
  );
};

export default Report;
